/**
 * Un PDF, escrito a mano: pages of text, rules and shaded bands in Helvetica, the font every PDF
 * reader has without being sent it (the owner, 2026-10-19: the prices «as a pdf that you can
 * download»). A price list needs nothing more, so there is no library for it: the file is plain
 * text but for the accented letters, in the order the format asks for.
 *
 * And photos (the owner, 2026-10-22: «the price catalog function should use the pictures we have
 * saved obviously»): a JPEG goes into the file as it is, which every reader draws, each photo once
 * however many times it is drawn.
 *
 * Measures are in points, 72 to the inch, from the page's top left as a screen's are; PDF's own
 * run up from the bottom, and this turns them round.
 */

export type Fuente = 'normal' | 'negrita';

/** A JPEG, three colours as a browser's canvas writes one, and its size in pixels. */
export interface Jpeg {
  datos: Uint8Array;
  ancho: number;
  alto: number;
}

/** US Letter, «carta», the paper a printer in Nicaragua has in it. */
export const CARTA = { ancho: 612, alto: 792 } as const;

/**
 * How wide each character from the space (32) to «~» (126) is, in thousandths of the type's size:
 * Adobe's metrics for Helvetica, which every reader draws it with.
 */
const HELVETICA = [
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556,
  556, 556, 556, 278, 278, 584, 584, 584, 556, 1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833,
  722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556, 333, 556, 556, 500, 556,
  556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556, 556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334,
  260, 334, 584,
];
const HELVETICA_NEGRITA = [
  278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556,
  556, 556, 556, 333, 333, 584, 584, 584, 611, 975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833,
  722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556, 333, 556, 611, 556, 611,
  556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611, 611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389,
  280, 389, 584,
];

/**
 * The characters past ASCII a shop's names and prices meet that are not plain accented letters:
 * their byte in WinAnsi, the encoding the two fonts are asked for, and their width in each.
 */
const OTROS: Record<string, readonly [byte: number, normal: number, negrita: number]> = {
  '€': [0x80, 556, 556],
  '…': [0x85, 1000, 1000],
  '‘': [0x91, 222, 278],
  '’': [0x92, 222, 278],
  '“': [0x93, 333, 500],
  '”': [0x94, 333, 500],
  '•': [0x95, 350, 350],
  '–': [0x96, 556, 556],
  '—': [0x97, 1000, 1000],
  '™': [0x99, 1000, 1000],
  '¡': [0xa1, 333, 333],
  '©': [0xa9, 737, 737],
  '«': [0xab, 556, 556],
  '®': [0xae, 737, 737],
  '°': [0xb0, 400, 400],
  '½': [0xbd, 834, 834],
  '¼': [0xbc, 834, 834],
  '·': [0xb7, 278, 278],
  'º': [0xba, 365, 365],
  'ª': [0xaa, 370, 370],
  '»': [0xbb, 556, 556],
  '¿': [0xbf, 611, 611],
  '×': [0xd7, 584, 584],
  'ß': [0xdf, 611, 611],
  'Æ': [0xc6, 1000, 1000],
  'æ': [0xe6, 889, 889],
  'Ø': [0xd8, 778, 778],
  'ø': [0xf8, 611, 611],
};

/** What does not fit the encoding is drawn as this, rather than as something else. */
const NINGUNO = '?';

/** Each character as WinAnsi draws it: its byte, and its width in each font. */
function glifo(c: string): readonly [byte: number, normal: number, negrita: number] {
  const codigo = c.codePointAt(0) ?? 63;
  if (codigo >= 32 && codigo <= 126) return [codigo, HELVETICA[codigo - 32]!, HELVETICA_NEGRITA[codigo - 32]!];
  const otro = OTROS[c];
  if (otro) return otro;
  // An accented letter of Latin-1 is its own byte, as wide as the letter under the accent; an «i»
  // under one loses its dot, and with it the narrow width.
  if (codigo >= 0xc0 && codigo <= 0xff) {
    const base = c.normalize('NFD')[0] ?? c;
    if (base === 'i') return [codigo, 278, 278];
    if (base !== c) {
      const [, normal, negrita] = glifo(base);
      return [codigo, normal, negrita];
    }
  }
  if (codigo >= 0xa0 && codigo <= 0xff) return [codigo, 556, 556];
  return glifo(NINGUNO);
}

/** The text as the fonts read it: one character for each byte, spaces of every kind a plain one. */
function glifos(s: string): (readonly [byte: number, normal: number, negrita: number])[] {
  return Array.from(s.normalize('NFC').replace(/[\s\p{Zs}]/gu, ' '), glifo);
}

/** The text in WinAnsi, as a string of bytes. */
export function aWinAnsi(s: string): string {
  return String.fromCharCode(...glifos(s).map(([byte]) => byte));
}

/** How wide the text is drawn, in points. */
export function anchoDeTexto(s: string, fuente: Fuente, tamano: number): number {
  const milesimas = glifos(s).reduce((a, [, normal, negrita]) => a + (fuente === 'negrita' ? negrita : normal), 0);
  return (milesimas * tamano) / 1000;
}

/** The text, cut with «…» where it would run past `maximo` points. */
export function recortar(s: string, maximo: number, fuente: Fuente, tamano: number): string {
  if (anchoDeTexto(s, fuente, tamano) <= maximo) return s;
  const letras = Array.from(s);
  while (letras.length > 0 && anchoDeTexto(`${letras.join('').trimEnd()}…`, fuente, tamano) > maximo) letras.pop();
  return `${letras.join('').trimEnd()}…`;
}

/** A number as the file writes it: two decimals at most, none when it has none. */
const num = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, ''));

/** A string of bytes inside a PDF's parentheses: its own parentheses and backslashes escaped. */
const literal = (bytes: string) => `(${bytes.replace(/[\\()]/g, (c) => `\\${c}`)})`;

/** Text a reader shows outside the page, its title: UTF-16 with its mark, so any name comes out whole. */
function textoDelLector(s: string): string {
  let hex = 'FEFF';
  for (let i = 0; i < s.length; i++) hex += s.charCodeAt(i).toString(16).padStart(4, '0').toUpperCase();
  return `<${hex}>`;
}

/** Bytes as the file's string, a character each; a slice at a time, as a call takes only so many. */
function comoCaracteres(datos: Uint8Array): string {
  let s = '';
  for (let i = 0; i < datos.length; i += 8_192) s += String.fromCharCode(...datos.subarray(i, i + 8_192));
  return s;
}

export class DocumentoPdf {
  private readonly paginas: string[][] = [];
  /** The photos each page draws, by the name its drawing calls them. */
  private readonly usadas: Set<string>[] = [];
  /** Each photo once, by the key it was drawn under, with the name the pages call it. */
  private readonly imagenes = new Map<string, { nombre: string; jpeg: Jpeg }>();
  private actual = -1;

  constructor(
    readonly ancho: number = CARTA.ancho,
    readonly alto: number = CARTA.alto,
  ) {}

  get cuantasPaginas(): number {
    return this.paginas.length;
  }

  /** A new page, where what follows is drawn. */
  nuevaPagina(): void {
    this.paginas.push([]);
    this.usadas.push(new Set());
    this.actual = this.paginas.length - 1;
  }

  /** Back to a page already made, to add to it: its number, once it is known how many there are. */
  irAPagina(i: number): void {
    if (i < 0 || i >= this.paginas.length) throw new RangeError(`No hay página ${i}`);
    this.actual = i;
  }

  /** Text on its baseline at `y`; `alinear: 'derecha'` ends it at `x`. `gris` 0 is black, 1 white. */
  texto(
    x: number,
    y: number,
    s: string,
    {
      fuente = 'normal',
      tamano = 10,
      gris = 0,
      alinear = 'izquierda',
    }: { fuente?: Fuente; tamano?: number; gris?: number; alinear?: 'izquierda' | 'derecha' } = {},
  ): void {
    const desde = alinear === 'derecha' ? x - anchoDeTexto(s, fuente, tamano) : x;
    this.dibujar(
      `BT /${fuente === 'negrita' ? 'F2' : 'F1'} ${num(tamano)} Tf ${num(gris)} g ${num(desde)} ${num(this.alto - y)} Td ${literal(aWinAnsi(s))} Tj ET`,
    );
  }

  /** A straight line. */
  linea(x1: number, y1: number, x2: number, y2: number, { grosor = 0.5, gris = 0.75 } = {}): void {
    this.dibujar(`${num(gris)} G ${num(grosor)} w ${num(x1)} ${num(this.alto - y1)} m ${num(x2)} ${num(this.alto - y2)} l S`);
  }

  /** A filled box whose top left is at `x`, `y`. */
  banda(x: number, y: number, ancho: number, alto: number, gris: number): void {
    this.dibujar(`${num(gris)} g ${num(x)} ${num(this.alto - y - alto)} ${num(ancho)} ${num(alto)} re f`);
  }

  /**
   * A JPEG filling a box whose top left is at `x`, `y`. The file keeps it once under `clave`,
   * however many times and on however many pages it is drawn.
   */
  imagen(x: number, y: number, ancho: number, alto: number, clave: string, jpeg: Jpeg): void {
    let imagen = this.imagenes.get(clave);
    if (!imagen) {
      imagen = { nombre: `Im${this.imagenes.size + 1}`, jpeg };
      this.imagenes.set(clave, imagen);
    }
    this.dibujar(`q ${num(ancho)} 0 0 ${num(alto)} ${num(x)} ${num(this.alto - y - alto)} cm /${imagen.nombre} Do Q`);
    this.usadas[this.actual]!.add(imagen.nombre);
  }

  private dibujar(operacion: string): void {
    if (this.actual < 0) this.nuevaPagina();
    this.paginas[this.actual]!.push(operacion);
  }

  /**
   * The file, `titulo` what a reader calls it. Objects 1 to 5 are the catalogue, the page tree,
   * the two fonts and the document's information; then each page and what is drawn on it; then
   * each photo. The table at the end says where each object starts, counted in bytes, which is why
   * the whole file is built as a string of bytes.
   */
  bytes(titulo: string): Uint8Array<ArrayBuffer> {
    if (this.paginas.length === 0) this.nuevaPagina();
    const pagina = (i: number) => 6 + 2 * i;
    const imagenes = [...this.imagenes.values()];
    const objetoDe = new Map(imagenes.map(({ nombre }, i) => [nombre, 6 + 2 * this.paginas.length + i]));
    const objetos = [
      '<< /Type /Catalog /Pages 2 0 R >>',
      `<< /Type /Pages /Kids [${this.paginas.map((_, i) => `${pagina(i)} 0 R`).join(' ')}] /Count ${this.paginas.length} >>`,
      '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
      '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>',
      `<< /Title ${textoDelLector(titulo)} /Producer (Abastece Ya) >>`,
    ];
    this.paginas.forEach((operaciones, i) => {
      const contenido = operaciones.join('\n');
      // Only the photos the page draws, each by the name its drawing calls it.
      const fotos = [...this.usadas[i]!].map((nombre) => `/${nombre} ${objetoDe.get(nombre)} 0 R`).join(' ');
      objetos.push(
        `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${num(this.ancho)} ${num(this.alto)}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >>${fotos ? ` /XObject << ${fotos} >>` : ''} >> /Contents ${pagina(i) + 1} 0 R >>`,
        `<< /Length ${contenido.length} >>\nstream\n${contenido}\nendstream`,
      );
    });
    for (const { jpeg } of imagenes) {
      objetos.push(
        `<< /Type /XObject /Subtype /Image /Width ${jpeg.ancho} /Height ${jpeg.alto} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.datos.length} >>\nstream\n${comoCaracteres(jpeg.datos)}\nendstream`,
      );
    }
    // The second line's four bytes past ASCII tell a program reading it that the file is not text.
    let archivo = '%PDF-1.4\n%\xe2\xe3\xcf\xd3\n';
    const dondeEmpieza: number[] = [];
    objetos.forEach((objeto, i) => {
      dondeEmpieza.push(archivo.length);
      archivo += `${i + 1} 0 obj\n${objeto}\nendobj\n`;
    });
    const tabla = archivo.length;
    archivo += `xref\n0 ${objetos.length + 1}\n0000000000 65535 f \n`;
    for (const d of dondeEmpieza) archivo += `${String(d).padStart(10, '0')} 00000 n \n`;
    archivo += `trailer\n<< /Size ${objetos.length + 1} /Root 1 0 R /Info 5 0 R >>\nstartxref\n${tabla}\n%%EOF\n`;
    return Uint8Array.from(archivo, (c) => c.charCodeAt(0));
  }
}
